import type { ReactNode } from 'react'

import type { Evaluation, Roadmap } from '@/shared/api/queries'
import { daysUntil } from '@/shared/format'
import { useLocalization } from '@/shared/i18n'
import type { Language, TranslationKey } from '@/shared/i18n'
import { AREAS, areaIcon, areaLabel } from '@/shared/model/areas'
import { byUrgency, urgencyOf } from '@/shared/model/urgency'
import type { Urgency } from '@/shared/model/urgency'
import { Badge, Button, Card, Icon, Stack, Stage, Text } from '@/shared/ui'

const SOON_DAYS = 30

const PLURAL_FORMS = ['one', 'few', 'many', 'other'] as const

type AreaSummary = { category: string; count: number; urgency: Urgency }

function rulesKey(language: Language, count: number): TranslationKey {
  const form = new Intl.PluralRules(language).select(count)
  const known = (PLURAL_FORMS as readonly string[]).includes(form) ? form : 'other'

  return `onboarding.payoff.rules.${known}` as TranslationKey
}

function byArea(roadmap: Roadmap): AreaSummary[] {
  const counted = new Map<string, AreaSummary>()

  const add = (evaluation: Evaluation, urgency: Urgency) => {
    const current = counted.get(evaluation.category)

    counted.set(evaluation.category, {
      category: evaluation.category,
      count: (current?.count ?? 0) + 1,
      urgency: current && byUrgency(current.urgency, urgency) <= 0 ? current.urgency : urgency,
    })
  }

  for (const evaluation of [...roadmap.now, ...roadmap.later]) {
    add(evaluation, urgencyOf(evaluation))
  }

  for (const evaluation of roadmap.done) {
    add(evaluation, 'done')
  }

  const known = AREAS.flatMap((area) => counted.get(area) ?? [])
  const rest = [...counted.values()].filter(
    (summary) => !(AREAS as readonly string[]).includes(summary.category),
  )

  return [...known, ...rest]
}

function dueSoon(roadmap: Roadmap): number {
  return [...roadmap.now, ...roadmap.later].filter((evaluation) => {
    if (!evaluation.deadline) {
      return false
    }

    const days = daysUntil(evaluation.deadline)

    return days !== undefined && days <= SOON_DAYS
  }).length
}

export function Payoff({
  rail,
  roadmap,
  onFinish,
}: {
  rail: ReactNode
  roadmap: Roadmap
  onFinish: () => void
}) {
  const { t, language } = useLocalization()

  const total = roadmap.now.length + roadmap.later.length + roadmap.done.length
  const areas = byArea(roadmap)
  const soon = dueSoon(roadmap)

  return (
    <Stage
      rail={rail}
      footer={
        <Button full onClick={onFinish}>
          {t('onboarding.payoff.finish')}
        </Button>
      }
    >
      <Card inverse className="payoff__card">
        <Text role="eyebrow" tone="muted">
          {t('onboarding.payoff.eyebrow')}
        </Text>

        <div className="payoff__total">
          <span className="payoff__number">{total}</span>
          <Text as="span" role="body-strong" tone="muted" className="payoff__caption">
            {t('onboarding.payoff.count')}
          </Text>
        </div>

        {areas.length > 0 ? (
          <Stack as="ul" gap="sm" className="payoff__areas">
            {areas.map((area) => (
              <li
                key={area.category}
                className={
                  area.urgency === 'neutral' ? 'payoff__area' : `payoff__area tone--${area.urgency}`
                }
              >
                <Icon size="sm">{areaIcon(area.category)}</Icon>
                <Text as="span" role="label" className="payoff__name">
                  {t(areaLabel(area.category))}
                </Text>
                <Badge>{t(rulesKey(language, area.count), { count: area.count })}</Badge>
              </li>
            ))}
          </Stack>
        ) : null}
      </Card>

      <Text role="caption" tone="muted" measure className="payoff__note">
        {total === 0
          ? t('onboarding.payoff.none')
          : soon > 0
            ? t('onboarding.payoff.soon', { count: soon })
            : t('onboarding.payoff.calm')}
      </Text>
    </Stage>
  )
}
